import React from 'react';
import {
    ArrowTrendingDownIcon,
    ArrowTrendingUpIcon,
    BanknotesIcon,
    ClockIcon,
    ReceiptPercentIcon,
    UserPlusIcon,
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import KpiMetricTile from '../../../common/kpiMetricTile/KpiMetricTile';
import { formatNumberWithCommas } from '../../../../utils/numbers';
import { DEFAULT_PLACEHOLDER as MONEY_PLACEHOLDER } from '../../../../constants/money.constants';
import styles from './styles/user-kpi-cards.module.scss';

/**
 * KPI row on the user-journey page, in two tiers.
 *
 * The first tier answers where the member stands right now — are they square
 * with us, and how much do they still owe. The second tier is the historical
 * totals: what left the bank and what came back.
 *
 * Colours come from `moneyClassName`, which resolves through MoneyController, so
 * nothing here decides a tone on its own. Every card also carries a text label,
 * because colour is never the only signal.
 *
 * NOTE: the advance/fee totals are window-based while the settlement figures are
 * lifetime. No admin UI sends a date range today, so they coincide; whoever adds
 * a date picker has to revisit this row.
 *
 * @param {Object} props
 * @param {Object} props.consumer Consumer payload from `fetchConsumerUser`.
 * @param {{ label: string, tone: string, sublabel: string|null }} props.repayment
 *   From `MoneyController.describeRepaymentStatus`.
 * @param {(value: unknown) => string} props.formatMoney
 * @param {(tone: string) => string} props.moneyClassName
 * @param {(amount: unknown) => string} props.toneForOwedAmount `MoneyController.toneForOwedAmount`
 * @param {(amount: unknown) => string} props.toneForSettledAmount `MoneyController.toneForSettledAmount`
 */
function UserKpiCards({ consumer, repayment, formatMoney, moneyClassName, toneForOwedAmount, toneForSettledAmount }) {
    // A settle row written before the split was recorded leaves the principal /
    // fee division unknown. Say so on the two cards derived from it instead of
    // presenting a partial figure as complete. Left uncoloured (the tile's
    // default muted grey) on purpose: it is a data-completeness caveat, not a
    // financial figure, so it must not inherit the value's green/red/neutral tone.
    //
    // `!== true` rather than `=== false`, so a service that does not report the
    // split at all is treated as incomplete rather than as complete-and-zero.
    const splitSublabel = consumer?.settledSplitComplete !== true ? 'Partial data' : undefined;

    // Every figure is passed through undefined on purpose: a field the service
    // did not send is not a zero. `formatMoney` renders it as `—`, and the tone
    // helpers read it as neutral — never red (nothing is known to be owed) and
    // never green (nothing is known to have settled). A member with genuinely
    // zero activity gets a real 0 from the service and reads $0.00, also neutral.
    //
    // This matters beyond the not-yet-deployed fields: `fetchConsumerUser`
    // swallows its error and returns `{}`, and the page seeds `consumer: {}`, so
    // a failed report reaches this row looking exactly like a real one. `?? 0`
    // here would turn that failure into "$0.00 advanced, $0.00 in fees" — four
    // confident claims about a member whose report never arrived.
    const awaitingAmount = consumer?.awaitingSettlementAmount;
    const volumeSettled = consumer?.volumeSettled;
    const feesSettled = consumer?.feesSettled;
    const advanceAmount = consumer?.advanceAmount;
    const feesAmount = consumer?.feesAmount;

    // A count, not money, so it does not go through `formatMoney` — but it needs
    // the same placeholder rather than `formatNumberWithCommas(undefined)`, which
    // would render "NaN".
    const totalAdvances = consumer?.totalAdvances;
    const totalAdvancesLabel = totalAdvances === null || totalAdvances === undefined
        ? MONEY_PLACEHOLDER
        : formatNumberWithCommas(totalAdvances);

    const primary = [
        {
            key: 'repayment',
            icon: <ClockIcon />,
            label: 'Repayment Status',
            value: repayment?.label ?? '—',
            // No HIGHLIGHT variant here: it forces the value white, which would
            // erase the one place where the tone carries meaning (green Paid,
            // red Overdue, neutral Pending).
            valueClassName: moneyClassName(repayment?.tone),
            sublabel: repayment?.sublabel ?? undefined,
            // Overdue's sublabel IS a figure — the amount owed and the day count —
            // and both must read red, same as the value above them.
            sublabelClassName: moneyClassName(repayment?.tone),
        },
        {
            key: 'pending',
            icon: <BanknotesIcon />,
            label: 'Pending Settlement',
            value: formatMoney(awaitingAmount),
            valueClassName: moneyClassName(toneForOwedAmount(awaitingAmount)),
        },
    ];

    const secondary = [
        {
            key: 'advances',
            icon: <UserPlusIcon />,
            label: 'Total Advances',
            value: totalAdvancesLabel,
        },
        {
            key: 'volume-advanced',
            icon: <ArrowTrendingUpIcon />,
            label: 'Total Volume Advanced',
            value: formatMoney(advanceAmount),
        },
        {
            key: 'volume-settled',
            icon: <ArrowTrendingDownIcon />,
            label: 'Total Volume Settled',
            value: formatMoney(volumeSettled),
            valueClassName: moneyClassName(toneForSettledAmount(volumeSettled)),
            sublabel: splitSublabel,
        },
        {
            key: 'fees-charged',
            icon: <ReceiptPercentIcon />,
            label: 'Total Fees Charged',
            value: formatMoney(feesAmount),
        },
        {
            key: 'fees-settled',
            icon: <CheckBadgeIcon />,
            label: 'Total Fees Settled',
            value: formatMoney(feesSettled),
            valueClassName: moneyClassName(toneForSettledAmount(feesSettled)),
            sublabel: splitSublabel,
        },
    ];

    const renderTile = (card, cardClassName, compact = false) => (
        <KpiMetricTile
            key={card.key}
            variant={card.variant}
            icon={card.icon}
            label={card.label}
            value={card.value}
            valueClassName={card.valueClassName}
            sublabel={card.sublabel}
            sublabelClassName={card.sublabelClassName}
            compact={compact}
            className={cardClassName}
        />
    );

    return (
        <div className={styles.wrap}>
            <section className={styles.tierPrimary}>
                {primary.map((card) => renderTile(card, styles.cardPrimary))}
            </section>
            <section className={styles.tierSecondary}>
                {secondary.map((card) => renderTile(card, styles.cardSecondary, true))}
            </section>
        </div>
    );
}

export default UserKpiCards;
